import React, { useRef, useCallback, useMemo, useState, useEffect, useLayoutEffect } from 'react'
// Note: Tooltip/TooltipTrigger/TooltipContent removed from outer tab wrapper
// due to React 19 + Radix composeRefs infinite setState loop (PopperAnchor).
// Tab title tooltip uses native `title` attribute instead. Inner pin-button
// Tooltip (single asChild level, no extra callback refs) is still safe.
import { useSortable } from '@dnd-kit/sortable'
import { X, Circle, Pin, PinOff, Layers, FileCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { Tooltip, TooltipTrigger, TooltipContent, WithTooltip } from '../../../ui/tooltip'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuTrigger,
} from '../../../ui/context-menu'
import { useShortcutText } from '../../../../hooks/useShortcutText'
import {
  QuickNotesFilledIcon,
  QuickNotesEmptyIcon,
  QuickNotesFilledOpenIcon,
  QuickNotesEmptyOpenIcon,
  DormantIcon,
  DormantOpenIcon,
  UpcomingIcon,
  UpcomingOpenIcon,
} from '../../../icons'
import type { DraggableTabProps } from './DraggableTab.types'
import { ProjectTabIcon, projectIdFromPath, useProjectTitleColor } from '../../../common/ProjectIcon'

// Some tab icons have a dedicated "active tab" variant (e.g. Quick Notes
// renders a violet, filled version in the sidebar's active state). Map the
// inactive icon component to its active counterpart so the active tab in the
// tabbar can render the matching variant.
const ACTIVE_ICON_MAP = new Map<unknown, unknown>([
  [QuickNotesFilledIcon, QuickNotesFilledOpenIcon],
  [QuickNotesEmptyIcon, QuickNotesEmptyOpenIcon],
  [DormantIcon, DormantOpenIcon],
  [UpcomingIcon, UpcomingOpenIcon],
])

// Background is now rendered via ::before pseudo-element in CSS with its own
// transition. Only structural/text transitions remain inline for dnd-kit compat.
const BASE_TRANSITIONS = 'border-top-left-radius 0.25s ease, border-top-right-radius 0.25s ease, border-bottom-left-radius 0.25s ease, border-bottom-right-radius 0.25s ease, color 0.25s ease, border-color 0.25s ease, margin 0.25s ease, padding 0.25s ease, height 0.25s ease'

const DraggableTabComponent: React.FC<DraggableTabProps> = ({
  tab,
  isActive,
  isFirstTab,
  isTearingOff,
  showClose = true,
  adjacency,
  snapX,
  onClick,
  onClose,
  onPin,
  onMiddleClick,
  onCloseOthers,
  onPromote,
  canClose = true,
  onHoverChange,
  dimmed,
  forceHideTooltip
}) => {
  const { t } = useTranslation()
  const shortcutText = useShortcutText()
  const pointerStart = useRef<{ x: number; y: number } | null>(null)
  const titleRef = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  // Suppress CSS transitions for one frame when returning from tear-off,
  // so padding/margin/height snap instantly instead of animating from 0.
  const prevTearingOffRef = useRef(isTearingOff ?? false)
  const [suppressTransition, setSuppressTransition] = useState(false)

  useEffect(() => {
    if (prevTearingOffRef.current && !isTearingOff) {
      setSuppressTransition(true)
      let id1 = 0, id2 = 0
      id1 = requestAnimationFrame(() => {
        id2 = requestAnimationFrame(() => {
          setSuppressTransition(false)
        })
      })
      prevTearingOffRef.current = false
      return () => { cancelAnimationFrame(id1); cancelAnimationFrame(id2) }
    }
    prevTearingOffRef.current = isTearingOff ?? false
    return undefined
  }, [isTearingOff])

  // Detect title text overflow for gradient fade
  // useLayoutEffect runs before paint to avoid visible flash
  useLayoutEffect(() => {
    const el = titleRef.current
    if (!el) return
    setIsTruncated(el.scrollWidth > el.clientWidth + 1)
  }, [tab.title])

  // ResizeObserver for ongoing size changes (window resize, sidebar toggle)
  // Skip updates while context menu is open to prevent re-render that resets menu highlight
  useEffect(() => {
    const el = titleRef.current
    if (!el) return
    const check = () => {
      if (contextMenuOpenRef.current) return
      setIsTruncated(el.scrollWidth > el.clientWidth + 1)
    }
    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: tab.id,
    disabled: false
  })

  // Track context menu open state via ref only (no state to avoid re-render
  // that would reset the context menu's highlighted item)
  const contextMenuOpenRef = useRef(false)

  // Suppress tooltips during drag and briefly after release,
  // so the close/pin tooltip doesn't flash when pointer is still over the button.
  const [suppressTooltips, setSuppressTooltips] = useState(false)
  const suppressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (isDragging) {
      if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current)
      setSuppressTooltips(true)
    } else if (suppressTooltips) {
      suppressTimerRef.current = setTimeout(() => setSuppressTooltips(false), 300)
    }
    return () => { if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current) }
  }, [isDragging]) // eslint-disable-line react-hooks/exhaustive-deps

  // Suppress transform transition at drag end: when DnD-Kit's sorting ends,
  // the displaced tab's transform goes from delta → 0. Without suppression,
  // the tab visibly slides back instead of snapping to its resting position.
  // Detection runs in useLayoutEffect (synchronous, before paint) so the
  // browser never sees the animated return. FLIP animations are unaffected
  // because DnD-Kit provides its own transition during FLIP frames.
  const prevDndTransitionRef = useRef<string | null>(null)
  const [suppressDragEndTransform, setSuppressDragEndTransform] = useState(false)

  useLayoutEffect(() => {
    const wasActive = Boolean(prevDndTransitionRef.current)
    const isActive = Boolean(transition)
    prevDndTransitionRef.current = transition ?? null

    if (wasActive && !isActive && !isDragging) {
      setSuppressDragEndTransform(true)
    }
  }, [transition, isDragging])

  useEffect(() => {
    if (suppressDragEndTransform) {
      let id1 = 0, id2 = 0
      id1 = requestAnimationFrame(() => {
        id2 = requestAnimationFrame(() => {
          setSuppressDragEndTransform(false)
        })
      })
      return () => { cancelAnimationFrame(id1); cancelAnimationFrame(id2) }
    }
    return undefined
  }, [suppressDragEndTransform])

  // Only use translate, ignore scaleX/scaleY to prevent tab distortion during drag.
  // Merge dnd-kit transition (transform only) with our CSS border-radius transitions.
  // `transform 0.2s ease` MUST be included so the browser is already watching for
  // transform changes before DnD-Kit's displacement happens. Without it, Chromium
  // won't animate when `transition` and `transform` both change in the same frame
  // (drag-start + displacement can be batched into one render).
  // At drag end, suppressDragEndTransform removes it to prevent visible slide-back.
  //
  // IMPORTANT: `transition: transform` must only be present when DnD-Kit actually
  // needs it (active drag or sort displacement). In Chromium, `transition: transform`
  // can promote the element to its own compositor layer, creating an implicit stacking
  // context — only relevant during DnD operations, not normal tab switching.
  //
  // Only the dragged tab (`isDragging`) and displaced tabs during sorting
  // (`transition` provided by DnD-Kit) get the transform transition.
  const needsTransformTransition = isDragging || Boolean(transition)
  const cssTransition = suppressDragEndTransform
    ? BASE_TRANSITIONS
    : needsTransformTransition
      ? `transform 0.2s ease, ${BASE_TRANSITIONS}`
      : BASE_TRANSITIONS

  // Active tab snap: override transform with snap position instead of cursor delta
  const useSnap = isActive && isDragging && snapX !== null && snapX !== undefined
  // Lock Y to 0 for the dragged tab so icon + label slide horizontally only —
  // the inactive pill underneath is anchored to the container bottom and the
  // mismatch (tab floating, pill rail-bound) looked off.
  const tabTranslateY = isDragging ? 0 : transform ? Math.round(transform.y) : 0
  const style = {
    transform: useSnap
      ? `translate3d(${snapX}px, 0, 0)`
      : transform
        ? `translate3d(${Math.round(transform.x)}px, ${tabTranslateY}px, 0)`
        // During sorting: keep an explicit inline translate3d(0,0,0) so the browser
        // can reliably transition between displaced and resting positions.
        // Without this, removing the inline transform falls back to the CSS cascade
        // value (translateZ(0)), and browsers may not animate that change.
        : transition
          ? 'translate3d(0, 0, 0)'
          : undefined,
    transition: suppressTransition
      ? 'none'
      : useSnap
        ? `transform 0.15s ease, ${cssTransition}`
        : transition
          ? `${transition}, ${cssTransition}`
          : cssTransition,
  }

  // Click detection: track pointer start position, fire click on pointerup if no drag
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    pointerStart.current = { x: e.clientX, y: e.clientY }
    // Call dnd-kit's onPointerDown if present
    listeners?.onPointerDown?.(e as unknown as PointerEvent)
  }, [listeners])

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!pointerStart.current) return
    const dx = Math.abs(e.clientX - pointerStart.current.x)
    const dy = Math.abs(e.clientY - pointerStart.current.y)
    pointerStart.current = null

    // Ignore clicks on action buttons (close, pin) — they handle their own onClick
    const target = e.target as HTMLElement
    if (target.closest('.tab-bar__tab-action')) return

    // Only treat as click if pointer barely moved (not a drag)
    if (dx < 5 && dy < 5) {
      if (e.button === 0) onClick(tab)
      if (e.button === 1) onMiddleClick(e as unknown as React.MouseEvent, tab)
    }
  }, [onClick, onMiddleClick, tab])

  // Double-click: preview → promote to permanent, permanent → pin
  const handleDoubleClick = useCallback(() => {
    if (tab.isPreview && onPromote) {
      onPromote(tab)
    } else if (!tab.isPreview) {
      onPin({} as React.MouseEvent, tab)
    }
  }, [tab, onPromote, onPin])

  const tabProjectId = projectIdFromPath(tab.path)
  const titleColor = useProjectTitleColor(tabProjectId)
  const Icon = isActive
    ? ((ACTIVE_ICON_MAP.get(tab.icon as unknown) as typeof tab.icon) ?? tab.icon)
    : tab.icon

  // Spread dnd-kit listeners but override onPointerDown with our wrapper
  const dndListeners = useMemo(() => {
    const l = { ...listeners }
    delete l.onPointerDown
    return l
  }, [listeners])

  const shortcut = shortcutText('⌘W')
  const closeOthersShortcut = shortcutText('⌘⇧W')

  const tabContent = (
    // Voller Titel (abgeschnitten) bzw. Vorschau-Hinweis. Stumm wie die übrigen Tab-Tooltips, solange sie
    // unterdrückt sind, und beim Ziehen.
    <WithTooltip
      content={!(suppressTooltips || forceHideTooltip || isDragging) && (isTruncated || tab.isPreview)
        ? [isTruncated && tab.title, tab.isPreview && t('tabs.previewHint')].filter(Boolean).join('\n')
        : undefined}
    >
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...dndListeners}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onDoubleClick={handleDoubleClick}
        onMouseEnter={!isActive && onHoverChange ? () => { if (!contextMenuOpenRef.current) onHoverChange(tab.path) } : undefined}
        onMouseLeave={!isActive && onHoverChange ? () => { if (!contextMenuOpenRef.current) onHoverChange(null) } : undefined}
        data-tab-id={tab.id}
        data-path={tab.path}
        data-space={tab.lifeSpace}
        data-label-color={titleColor ?? undefined}
        className={clsx(
          'tab-bar__tab',
          isActive && 'tab-bar__tab--active',
          isFirstTab && 'tab-bar__tab--first-active',
          isTearingOff && 'tab-bar__tab--tearing-off',
          tab.isPinned && 'tab-bar__tab--pinned',
          tab.isPreview && 'tab-bar__tab--preview',
          isDragging && !isActive && 'tab-bar__tab--dragging',
          isDragging && isActive && 'tab-bar__tab--active-dragging',
          adjacency && `tab-bar__tab--${adjacency}`,
          dimmed && 'tab-bar__tab--dimmed'
        )}
      >
        {Icon && (
          <span className="tab-bar__tab-icon">
            {tabProjectId
              ? <ProjectTabIcon projectId={tabProjectId} size={16} fallback={<Icon size={16} />} />
              : <Icon size={16} />}
          </span>
        )}
        <span ref={titleRef} role="presentation" data-title={tab.title} className={clsx('tab-bar__tab-title', isTruncated && 'tab-bar__tab-title--fading', tab.isPreview && 'tab-bar__tab-title--preview')}>{tab.title}</span>

        {tab.isDirty && (
          <span className="tab-bar__tab-dirty">
            <Circle size={6} fill="currentColor" />
          </span>
        )}

        <div className={`tab-bar__tab-actions${!showClose && !tab.isPinned ? ' tab-bar__tab-actions--hover-only' : ''}`}>
          {tab.isPinned ? (
            <Tooltip open={suppressTooltips || forceHideTooltip ? false : undefined}>
              <TooltipTrigger asChild>
                <button
                  className="tab-bar__tab-action tab-bar__tab-action--pin"
                  onClick={(e) => onPin(e, tab)}
                  aria-label={t('tabs.unpinTab')}
                >
                  <Pin size={16} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">{t('tabs.unpinTab')}</TooltipContent>
            </Tooltip>
          ) : canClose && !tab.isPreview ? (
            <button
              className="tab-bar__tab-action tab-bar__tab-action--close"
              onClick={(e) => onClose(e, tab)}
              aria-label={t('tabs.closeTab', { shortcut })}
            >
              <X size={16} />
            </button>
          ) : tab.isPreview ? (
            <button
              className="tab-bar__tab-action tab-bar__tab-preview-dot"
              onClick={(e) => onClose(e, tab)}
              aria-label={t('tabs.closeTab', { shortcut })}
            >
              <X size={14} />
            </button>
          ) : null}
        </div>
      </div>
    </WithTooltip>
  )

  return (
    <ContextMenu onOpenChange={(open) => {
      contextMenuOpenRef.current = open
    }}>
      <ContextMenuTrigger asChild>
        {tabContent}
      </ContextMenuTrigger>
      <ContextMenuContent>
        {tab.isPreview && onPromote && (
          <ContextMenuItem onSelect={() => onPromote(tab)}>
            <FileCheck size={14} className="context-menu-content__item-icon" />
            {t('tabs.keepTab')}
          </ContextMenuItem>
        )}
        <ContextMenuItem onSelect={() => onPin({} as React.MouseEvent, tab)}>
          {tab.isPinned
            ? <PinOff size={14} className="context-menu-content__item-icon" />
            : <Pin size={14} className="context-menu-content__item-icon" />
          }
          {tab.isPinned ? t('tabs.unpinTab') : t('tabs.pinTab')}
        </ContextMenuItem>
        <ContextMenuSeparator />
        {onCloseOthers && (
          <ContextMenuItem onSelect={() => onCloseOthers!(tab)}>
            <Layers size={14} className="context-menu-content__item-icon" />
            {t('tabs.closeOthers')}
            <ContextMenuShortcut>{closeOthersShortcut}</ContextMenuShortcut>
          </ContextMenuItem>
        )}
        {canClose && (
          <ContextMenuItem onSelect={() => onClose({} as React.MouseEvent, tab)}>
            <X size={14} className="context-menu-content__item-icon" />
            {t('tabs.closeTab', { shortcut: '' }).replace(/\s*\(\)\s*$/, '')}
            <ContextMenuShortcut>{shortcut}</ContextMenuShortcut>
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  )
}

/**
 * Individual sortable tab rendered inside the {@link TabBar}.
 *
 * @description
 * Wraps a single tab with dnd-kit's `useSortable` for drag-and-drop reordering.
 * Handles several interaction subtleties:
 *
 * - **Click vs. drag detection** — tracks pointer start position and only fires
 *   `onClick` if the pointer moved less than 5px (otherwise it's a drag)
 * - **Snap transform** — when the active tab is dragged, replaces dnd-kit's
 *   cursor-following transform with a discrete snap offset (`snapX` prop)
 * - **Tear-off animation suppression** — when a tab returns from tear-off,
 *   CSS transitions are suppressed for one frame to prevent visual pop-in
 * - **Drag-end transform suppression** — prevents displaced tabs from visibly
 *   sliding back to their resting position after a sort completes
 * - **Text overflow detection** — applies a gradient fade when the title overflows
 * - **Adjacency classes** — adds `left-of-active` / `right-of-active` CSS classes
 *   for neighbor tabs to adjust their border-radius
 *
 * Memoized with default shallow comparison (tab objects are stable references).
 *
 * @example
 * <DraggableTab
 *   tab={tab}
 *   isActive={activeTab?.id === tab.id}
 *   showClose={!compactMode}
 *   onClick={handleTabClick}
 *   onClose={handleCloseClick}
 *   onPin={handlePinClick}
 *   onMiddleClick={handleMiddleClick}
 *   onCloseOthers={onCloseOthers}
 * />
 */
export const DraggableTab = React.memo(DraggableTabComponent)
